import dayjs, { Dayjs } from 'dayjs';

export interface RawOpenHoursDay {
  no?: string | null;
  lidz?: string | null;
  closed?: boolean | string | null;
}

export type OpenHoursInput =
  | RawOpenHoursDay[]
  | Record<string, RawOpenHoursDay | undefined>
  | string
  | null
  | undefined;

const DAYS_IN_WEEK = 7;

export const normalizeOpenHours = (
  openHours: OpenHoursInput
): (RawOpenHoursDay | undefined)[] => {
  let value: unknown = openHours;

  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      value = null;
    }
  }

  if (!value || typeof value !== 'object')
    return Array(DAYS_IN_WEEK).fill(undefined);

  if (Array.isArray(value)) {
    const days = value as (RawOpenHoursDay | undefined)[];
    return Array.from({ length: DAYS_IN_WEEK }, (_, i) => days[i]);
  }

  const record = value as Record<string, RawOpenHoursDay | undefined>;
  const isOneBased = '7' in record && !('0' in record);

  return Array.from({ length: DAYS_IN_WEEK }, (_, i) => {
    const day = record[String(isOneBased ? i + 1 : i)];
    if (!day) return undefined;
    return day.closed === undefined && !day.no && !day.lidz
      ? { ...day, closed: true }
      : day;
  });
};

type DayJsOpenHours = Record<
  number,
  { no: Dayjs | null; lidz: Dayjs | null; closed: boolean }
>;

const toDayJsOpenHours = (
  openHours: OpenHoursInput,
  parse: (time: string) => Dayjs
): DayJsOpenHours => {
  const result: DayJsOpenHours = {};

  normalizeOpenHours(openHours).forEach((day, i) => {
    result[i] = {
      no: day?.no ? parse(day.no) : null,
      lidz: day?.lidz ? parse(day.lidz) : null,
      closed: !!day?.closed,
    };
  });

  return result;
};

export const convertToDayJs = (configOpenHours: OpenHoursInput) =>
  toDayJsOpenHours(configOpenHours, (time) => dayjs(time, 'HH:mm'));

export const convertISOToDayJs = (configOpenHours: OpenHoursInput) =>
  toDayJsOpenHours(configOpenHours, (time) =>
    dayjs(dayjs(time).format('HH:mm'), 'HH:mm')
  );
